import PageStoryImage from 'components/PageStoryImage';
import PageIntroBand from 'components/PageIntroBand';
import './AboutPage.css';
import HeroBackground from 'components/HeroBackground';
import {
  BarChart3,
  FileText,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Plus,
  SearchCheck,
  ShieldCheck,
  Settings,
  Users,
  Wrench,
} from 'lucide-react';
import { useReveal } from 'hooks/useReveal';
import { useSiteContent } from 'hooks/useSiteContent';
import HeroScrollRail from 'components/HeroScrollRail';
import SiteHeroCtas, {
  HERO_CTA_PRIMARY_CLASS,
  HERO_CTA_SECONDARY_CLASS,
  PAGE_HERO_CONTAINER_CLASS,
  PAGE_HERO_CTA_ROW_CLASS,
  PAGE_HERO_EYEBROW_CLASS,
} from 'components/SiteHeroCtas';

const ABOUT_MARQUEE_ITEMS = [
  "Driver files",
  "Safety follow-up",
  "Freight paperwork",
  "Practical training",
  "Recruiting processes",
  "TMS workflows",
  "Management visibility",
  "Clear responsibilities",
];

const ABOUT_HERO_CONTENT = {
  eyebrow: "About Magellan International",
  title: "The business behind trucking.",
  body: "Magellan International helps US trucking companies organize the work behind every load: operational documentation, team training and clear responsibility for daily decisions.\n\nOur team’s experience includes work with more than 30 companies before Magellan International was established. Each member brings more than 10 years of experience.",
  primaryCtaLabel: "View paperwork packages",
  primaryCtaPath: "/paperwork",
  secondaryCtaLabel: "",
  secondaryCtaPath: "",
  tertiaryCtaLabel: "",
  tertiaryCtaPath: "",
};

const ABOUT_HERO_SECTION_CLASS = 'relative flex min-h-[100svh] w-full items-center overflow-hidden md:h-screen md:min-h-[680px]';

function renderGoldHeroTitle(title) {
  const words = String(title || '').trim().split(/\s+/).filter(Boolean);
  const goldStartIndex = Math.max(0, words.length - 3);
  const regularWords = words.slice(0, goldStartIndex);
  const goldPhrase = words.slice(goldStartIndex).join(' ');

  return (
    <>
      {regularWords.map((word, index) => (
        <span key={`${word}-${index}`} aria-hidden="true" className="mr-3 inline-block overflow-hidden align-bottom sm:mr-4">
          <span className="inline-block">
            {word}
          </span>
        </span>
      ))}
      <span aria-hidden="true" className="mr-3 inline-block overflow-hidden align-bottom sm:mr-4">
        <span className="inline-block italic shimmer-text">
          {goldPhrase}
        </span>
      </span>
    </>
  );
}

const SYSTEM_DIAGRAM_ITEMS = [
  { label: 'Safety', y: 70, dotX: 602, dotY: 120, icon: ShieldCheck, path: 'M240 70 H340 C465 70 495 120 602 120' },
  { label: 'Compliance', y: 140, dotX: 562, dotY: 168, icon: FileText, path: 'M240 140 H355 C465 140 485 168 562 168' },
  { label: 'Maintenance', y: 210, dotX: 543, dotY: 216, icon: Wrench, path: 'M240 210 H370 C470 210 485 216 543 216' },
  { label: 'Drivers', y: 280, dotX: 540, dotY: 264, icon: Users, path: 'M240 280 H370 C470 280 485 264 540 264' },
  { label: 'Operations', y: 350, dotX: 552, dotY: 312, icon: Settings, path: 'M240 350 H355 C465 350 485 312 552 312' },
  { label: 'Finance', y: 420, dotX: 602, dotY: 380, icon: BarChart3, path: 'M240 420 H340 C465 420 495 380 602 380' },
];

const FOCUS_AREAS = [
  ["Driver and safety compliance", "Driver qualification (DQ) files, onboarding records, medical cards, motor vehicle records (MVRs), Drug and Alcohol Clearinghouse documentation and annual reviews."],
  ["Hours of service and operations", "Hours of service (HOS) records, electronic logging device (ELD) supporting documents, log edits, unassigned miles and review routines."],
  ["Maintenance and equipment", "Driver vehicle inspection reports (DVIRs), preventive maintenance schedules, annual inspections, repair tracking and out-of-service follow-up."],
  ["Risk and audit organization", "Accident registers, crash files, roadside inspection reports, corrective actions, Compliance, Safety, Accountability (CSA) and DataQs evidence packets, and root-cause analysis."],
  ["Business credentials", "Operating authority, MCS-150 records, portal access, insurance documentation, BOC-3 paperwork and renewal calendars."],
  ["Process control", "Policies, standard operating procedures (SOPs), employee acknowledgments, task trackers and management review records."],
];

const OPERATIONAL_QUESTIONS = [
  'What must be done each day?',
  'What needs to be checked?',
  'What does completed work look like?',
  'Who needs to receive the information?',
  'When should a problem be escalated?',
];

const PACKAGE_OPTIONS = [
  ["Basic", "Operational policies, procedures, forms and tracking workbooks for a consistent way of handling daily paperwork."],
  ["Standard", "Basic plus paperwork for an ISO 9001 quality management system, additional policies and procedures, and applicable specialist paperwork agreed after review."],
  ["Premium", "Standard plus paperwork for ISO 14001 environmental and ISO 45001 occupational health and safety management systems, further expanded policies and procedures, and applicable specialist freight controls agreed after review."],
];

const COMMON_QUESTIONS = [
  {
    title: 'What makes Magellan different?',
    copy: "We look at what happens between departments, where an incomplete record or unanswered question can hold up someone else’s job. That means considering the handoff as well as the individual task, with the aim of reducing routine questions that return to the owner.",
  },
  {
    title: 'Where should a carrier start?',
    copy: "Compare Basic, Standard and Premium in the Paperwork section. All three include a review of your existing paperwork after payment is confirmed. We send written findings and a document-gap list, then meet with you to discuss the package and any additional work you need.",
  },
  {
    title: 'Is this legal advice?',
    copy: 'No. Magellan provides operational documentation, consulting, training, and support materials. Legal questions should be reviewed with a qualified attorney.',
  },
  {
    title: 'Does Magellan guarantee audit results or DataQs outcomes?',
    copy: 'No. No paperwork system can guarantee agency, court, audit, insurance, broker, shipper, or enforcement outcomes. The value is a stronger structure, cleaner records, clearer ownership, and review-ready documentation.',
  },
];

function SectionLabel({ children }) {
  return (
    <div className="reveal flex items-center gap-3 mb-5">
      <div className="w-8 h-px bg-gold" />
      <p className="text-[11px] uppercase tracking-[0.28em] text-accent-ink/80 font-body">{children}</p>
    </div>
  );
}

function CtaButtons({ center = false, content }) {
  const ctaContent = content || ABOUT_HERO_CONTENT;

  return (
    <SiteHeroCtas
      content={ctaContent}
      className={`${PAGE_HERO_CTA_ROW_CLASS} ${center ? 'justify-center' : ''}`}
      primaryClassName={HERO_CTA_PRIMARY_CLASS}
      secondaryClassName={HERO_CTA_SECONDARY_CLASS}
      tertiaryClassName={HERO_CTA_SECONDARY_CLASS}
    />
  );
}

function AboutIntroBand() {
  return (
    <PageIntroBand
      testId="about-page-intro"
      label="Why Magellan exists"
      title="Growth needs structure."
      backgroundText="ABOUT"
      paragraphs={[
        'More trucks bring more records, handoffs and decisions. Without a shared way of working, each department keeps turning back to the owner.',
        'We help turn operating knowledge into written procedures, practical training and clear responsibilities, so the team can follow the work from start to finish.',
      ]}
    />
  );
}

function SystemDiagram() {
  return (
    <div className="reveal reveal-delay-2 hidden lg:flex min-h-[470px] items-center justify-center xl:min-h-[510px]">
      <div className="relative w-[min(56vw,900px)] max-w-[900px] lg:-ml-8 xl:-ml-10">
        <svg
          viewBox="0 0 900 500"
          role="img"
          aria-label="Safety, compliance, maintenance, drivers, operations, and finance connected into one operating system"
          className="h-auto w-full overflow-visible drop-shadow-[0_30px_70px_rgba(0,0,0,0.45)]"
        >
          <defs>
            <linearGradient id="aboutDiagramLine" x1="240" x2="555" y1="0" y2="0" gradientUnits="userSpaceOnUse">
              <stop stopColor="#27313c" stopOpacity="0.45" />
              <stop offset="0.55" stopColor="#27313c" stopOpacity="0.9" />
              <stop offset="1" stopColor="#27313c" stopOpacity="1" />
            </linearGradient>
            <filter id="aboutDiagramGlow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {SYSTEM_DIAGRAM_ITEMS.map(({ label, y, dotX, dotY, path }) => (
            <g key={label}>
              <text
                x="95"
                y={y + 6}
                fill="#F8F4E8"
                fillOpacity="0.88"
                fontFamily="Outfit, sans-serif"
                fontSize="16"
                fontWeight="700"
                letterSpacing="2.8"
              >
                {label.toUpperCase()}
              </text>
              <path d={path} fill="none" stroke="url(#aboutDiagramLine)" strokeWidth="2.3" strokeLinecap="round" />
              <circle cx={dotX} cy={dotY} r="8.2" fill="#101418" stroke="#27313c" strokeWidth="3" filter="url(#aboutDiagramGlow)" />
            </g>
          ))}

          <circle cx="705" cy="250" r="158" fill="rgba(16,20,24,0.42)" stroke="#27313c" strokeOpacity="0.52" strokeWidth="1.7" strokeDasharray="1 10" strokeLinecap="round" />
          <circle cx="705" cy="250" r="136" fill="rgba(16,20,24,0.38)" stroke="#27313c" strokeOpacity="0.92" strokeWidth="2.6" />
          <circle cx="705" cy="250" r="124" fill="none" stroke="#27313c" strokeOpacity="0.28" strokeWidth="1" />
          <circle cx="705" cy="250" r="106" fill="none" stroke="#F8F4E8" strokeOpacity="0.08" strokeWidth="1" />

          <image
            href="/images/brand/magellan-compass-mark.png"
            x="653"
            y="154"
            width="104"
            height="104"
            preserveAspectRatio="xMidYMid meet"
          />

          <text
            x="705"
            y="278"
            fill="#F8F4E8"
            fontFamily="Playfair Display, serif"
            fontSize="14"
            fontWeight="500"
            letterSpacing="0.45"
            textAnchor="middle"
            textLength="92"
            lengthAdjust="spacing"
          >
            ONE SYSTEM.
          </text>
          <text
            x="705"
            y="300"
            fill="#F8F4E8"
            fontFamily="Playfair Display, serif"
            fontSize="14"
            fontWeight="500"
            letterSpacing="0.45"
            textAnchor="middle"
            textLength="128"
            lengthAdjust="spacing"
          >
            COMPLETE CLARITY.
          </text>
        </svg>

        {SYSTEM_DIAGRAM_ITEMS.map(({ label, y, icon: Icon }) => (
          <Icon
            key={label}
            className="absolute h-7 w-7 text-accent-ink drop-shadow-[0_2px_12px_rgba(0,0,0,0.75)]"
            strokeWidth={1.55}
            style={{
              left: `${(40 / 900) * 100}%`,
              top: `${((y - 18) / 500) * 100}%`,
            }}
            aria-hidden="true"
          />
        ))}
      </div>
    </div>
  );
}

function TrainingResult({ className }) {
  return <p className={`about-training-result ${className}`}>A path to independence for the employee.<br /><strong>Room to manage for the owner.</strong></p>;
}

export default function AboutPage() {
  const ref = useReveal();
  const cmsHero = useSiteContent('aboutHero');
  const focus = useSiteContent('aboutFocus');

  const hero = ABOUT_HERO_CONTENT;
  const heroTitle = hero.title;
  const heroParagraphs = hero.body.split('\n\n');
  const heroImageUrl = cmsHero.imageUrl;

  return (
    <div ref={ref} className="min-h-screen bg-canvas">
      <section data-theme-surface="dark" data-testid="about-hero" className={`${ABOUT_HERO_SECTION_CLASS} site-image-hero`}>
        <HeroBackground imageUrl={heroImageUrl} imageAlt={cmsHero.imageAlt} />
        <div className="absolute inset-0 grain-overlay" />

        <div data-hero-text-parallax="true" className={PAGE_HERO_CONTAINER_CLASS}>
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.86fr)_minmax(0,1.14fr)] gap-8 xl:gap-10 items-center">
            <div>
              <p className={PAGE_HERO_EYEBROW_CLASS}>{hero.eyebrow}</p>
              <h1 aria-label={heroTitle} className="reveal reveal-delay-1 max-w-3xl font-display text-4xl leading-[1.02] tracking-tight text-ink sm:text-5xl lg:text-[3.35rem] xl:text-[3.85rem]">
                {renderGoldHeroTitle(heroTitle)}
              </h1>

              <div className="reveal reveal-delay-2 mb-8 max-w-2xl space-y-4 font-body text-sm leading-7 text-copy/58 md:text-base md:leading-8">
                {heroParagraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              </div>

              <CtaButtons content={hero} />
            </div>

            <SystemDiagram />
          </div>
        </div>
        <HeroScrollRail />
      </section>

      <div className="topic-rail overflow-hidden border-y border-ink/10 bg-surface">
        <div className="marquee-container">
          <div className="marquee-track">
            {[...ABOUT_MARQUEE_ITEMS, ...ABOUT_MARQUEE_ITEMS].map((item, index) => (
              <span key={`${item}-${index}`} className="inline-flex items-center gap-3 px-9 font-body text-[9px] uppercase tracking-[0.44em] text-copy/24 whitespace-nowrap">
                {item}
                <span className="h-1 w-1 rotate-45 bg-gold/45" />
              </span>
            ))}
          </div>
        </div>
      </div>

      <AboutIntroBand />

      <div className="about-story">
        <section data-testid="about-owner" className="about-chapter">
          <div className="about-shell">
            <SectionLabel>01 / The owner bottleneck</SectionLabel>
            <div className="about-heading-row">
              <h2>Every question.<br />Back to you.</h2>
              <p>A dispatcher needs a decision. Safety needs a document. Accounting needs an explanation. Each question seems small. Together, they consume your working day.</p>
            </div>
            <PageStoryImage testId="about-owner-image" src="/images/about/carrier-yard.jpg" alt="A person looking over a row of parked trucks at dusk" position="center 58%" />
            <div className="about-bottleneck reveal">
              <div className="about-requests">
                {[['Dispatch', '“Can we send this truck?”', Settings], ['Safety', '“Where is the document?”', ShieldCheck], ['Accounting', '“Who can explain this?”', BarChart3], ['New employee', '“What do I do next?”', Users]].map(([name, question, Icon]) => (
                  <div className="about-request" key={name}><Icon size={20} strokeWidth={1.4} aria-hidden="true" /><span>{name}<small>{question}</small></span><ArrowRight size={16} className="about-request-arrow" aria-hidden="true" /></div>
                ))}
              </div>
              <div className="about-owner-hub">
                <span className="about-kicker">One person. Every department.</span>
                <span className="about-owner-word">You.</span>
                <p>Dispatching. Checking. Explaining.<br />Solving. Training. Starting again.</p>
              </div>
              <div className="about-owner-cost"><span className="about-kicker">What gets pushed aside</span><h3>Time to run<br />{' '}the business.</h3><p>Planning, developing people and building a company that can work without your constant involvement.</p></div>
            </div>
            <p className="about-takeaway">When the company cannot function without you, ownership becomes another full-time job. <em>Build an asset that can operate beyond you.</em></p>
          </div>
        </section>

        <section data-testid="about-operational" className="about-chapter about-chapter-tint">
          <div className="about-shell">
            <SectionLabel>02 / Knowledge that stays in the company</SectionLabel>
            <div className="about-heading-row"><h2>Make the work clear.<br />Then teach it.</h2><p>When instructions are passed on only in conversation, useful knowledge can leave with an employee. Writing it down gives colleagues a reference they can use long after training.</p></div>
            <PageStoryImage testId="about-learning-image" src="/images/careers/document-review.jpg" alt="Reviewing documents and a checklist at a desk" position="center 63%" />
            <div className="about-learning">
              <div className="about-questions"><h3>A role should answer five questions.</h3>{OPERATIONAL_QUESTIONS.map((question,index)=><div key={question}><span>0{index+1}</span><p>{question}</p></div>)}<TrainingResult className="about-training-result--desktop" /></div>
              <div data-testid="about-experience" className="about-training"><BookOpen size={30} strokeWidth={1.2} aria-hidden="true" /><h3>A login is a starting point.<br />Understanding the job takes training.</h3><p>A new hire needs more than access to files and software. Role-specific training connects the written procedure to a real task, a completed example and the decisions the employee can make.</p><ol>{[['Explain', 'Define the task, its purpose and the person responsible.'], ['Demonstrate', 'Show the process, the checks and a completed example.'], ['Practice', 'Let the employee work through the task using the written procedure.'], ['Support', 'Set a clear point of contact for questions and exceptions.']].map(([title,copy])=><li key={title}><CheckCircle2 size={16} aria-hidden="true" /><span><strong>{title}</strong>{copy}</span></li>)}</ol><TrainingResult className="about-training-result--mobile" /></div>
            </div>
          </div>
        </section>

        <section data-testid="about-technology" className="about-chapter">
          <div className="about-shell">
            <SectionLabel>03 / The standard for our software</SectionLabel>
            <div className="about-heading-row"><h2>Technology should<br />move the work forward.</h2><p>CarrierFlow TMS is our transportation management system for daily carrier work. It helps the team find records, review information and hand work to the next department.</p></div>
            <div className="about-workflow reveal">
              <div className="about-workflow-before"><span className="about-kicker">The daily friction</span><h3>Chasing a record.<br />Holding up a truck.</h3><p>Safety needs a record before a review can be completed. The file is missing, so someone calls the driver, asks a colleague and waits. If dispatch depends on that review, the truck waits too.</p><div className="about-friction-tags"><span>Call the driver</span><span>Ask a colleague</span><span>Wait for the file</span></div></div>
              <span className="about-workflow-arrow" aria-hidden="true"><ArrowRight size={26} /></span>
              <div className="about-workflow-after"><span className="about-kicker">A useful workflow</span><h3>The right information.<br />A clear next action.</h3><p>Put the required record, the responsible person and the next step in a workflow the team can follow.</p><ul><li>Can the responsible person find the record?</li><li>Is the next task clear?</li><li>Can the next department see what is complete?</li><li>Can the owner review exceptions without chasing updates?</li></ul></div>
            </div>
            <p className="about-takeaway">The measure is practical: <em>what gets better in the working day?</em></p>
          </div>
        </section>

        <section data-testid="about-connected-work" className="about-chapter about-chapter-tint">
          <div className="about-shell">
            <SectionLabel>04 / One approach</SectionLabel>
            <h2 className="about-wide-heading">People, knowledge and systems.<br /><em>Built to work together.</em></h2>
            <div className="about-pillars">{[[SearchCheck, 'Consulting', 'Find the gaps.', 'Identify where responsibilities overlap, handoffs break down or employees need clearer instructions.'],[FileText,'Documentation','Make it repeatable.','Give employees procedures and forms they can refer to while completing recurring tasks.'],[BookOpen,'Training','Prepare the people.','Help new and existing employees learn a role and use the company’s procedures in their daily work.'],[Settings,'Software','Connect the work.','Build CarrierFlow TMS around the records, task status and department handoffs the work requires.']].map(([Icon,title,line,copy],index)=><article key={title}><div className="about-pillar-top"><Icon size={24} strokeWidth={1.3} aria-hidden="true"/><span>0{index+1}</span></div><h3>{title}</h3><strong>{line}</strong><p>{copy}</p></article>)}</div>
            <p className="about-connection-note">Paperwork packages, Courses and CarrierFlow TMS have their own scope and purchase terms. Buying one does not automatically include the others.</p>
          </div>
        </section>

        <section data-testid="about-focus" className="about-chapter">
          <div className="about-shell">
            <SectionLabel>05 / The operating detail</SectionLabel>
            <div className="about-heading-row"><h2>{focus.title || 'What Magellan focuses on'}</h2><p>{focus.body || 'We focus on the records, operating procedures and department responsibilities US carriers need to manage daily work. These are the areas that shape our documentation, training and process support.'}</p></div>
            <div className="about-detail-grid">{FOCUS_AREAS.map(([title,copy],index)=><details key={title}><summary><span className="about-detail-number">0{index+1}</span><h3>{title}</h3><Plus size={18} aria-hidden="true" /></summary><p>{copy}</p></details>)}</div>
            <p className="about-footnote">CarrierFlow TMS workflow mapping, recruiting processes and management tracking can involve work beyond the selected paperwork package. Package inclusions are described in the Paperwork section.</p>
          </div>
        </section>

        <section data-testid="about-how" className="about-chapter about-chapter-tint">
          <div className="about-shell">
            <SectionLabel>06 / A place to start</SectionLabel>
            <div className="about-heading-row"><h2>Find the paperwork<br />your company needs.</h2><p>Compare Basic, Standard and Premium. Each includes a paperwork review after confirmed payment, a written report and a meeting to recommend the right scope. The management-system paperwork does not provide ISO certification.</p></div>
            <div className="about-packages">{PACKAGE_OPTIONS.map(([title,copy],index)=><article key={title}><span className="about-kicker">0{index+1}</span><h3>{title}</h3><p>{copy}</p></article>)}</div>
            <CtaButtons content={hero} />
          </div>
        </section>

        <section data-testid="about-common-questions" className="about-chapter about-answers">
          <div className="about-shell">
            <SectionLabel>Before we work together</SectionLabel>
            <h2>Common questions.</h2>
            <div className="about-faq">{COMMON_QUESTIONS.map(({title,copy})=><details key={title}><summary><h3>{title}</h3><Plus size={20} aria-hidden="true" /></summary><p>{copy}</p></details>)}</div>
            <aside data-testid="about-not-us" className="about-legal"><h3>What Magellan is not</h3><div><p>Magellan provides operational documentation, systems frameworks, workflow design, consulting, and training materials. The work is educational and operational in nature.</p><p>Magellan does not provide legal advice, agency representation, citation defense, court representation, civil penalty response, safety rating appeals, employment-law guidance, contract interpretation, or state-specific legal analysis. Legal matters should always be directed to a qualified attorney.</p></div></aside>
          </div>
        </section>
      </div>

      <section data-testid="about-final-cta" data-theme-surface="dark" className="relative py-28 md:py-36 overflow-hidden">
        <img
          src="/images/about/truck-road.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-canvas via-canvas/88 to-canvas/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-canvas via-transparent to-canvas/65" />
        <div className="absolute inset-0 grain-overlay" />

        <div className="site-content-shell relative z-10">
          <div className="max-w-5xl">
            <SectionLabel>Our purpose</SectionLabel>
            <h2 className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.02] mb-7">
              Give yourself room to run the business.
            </h2>
            <div className="reveal reveal-delay-2 font-body text-copy/58 leading-[1.85] max-w-3xl mb-10 space-y-5">
              <p>We help US trucking companies build a shared way of working that remains in the business as people join, roles change and the company grows.</p>
              <p>Employees need written guidance they can return to when a colleague is away or an unfamiliar situation comes up.</p>
              <p>Owners need enough visibility to judge the result and decide when their involvement is necessary.</p>
              <p>The question guiding our work:</p>
              <p className="font-display text-3xl leading-tight text-ink">Can the team complete the work, explain the result and keep moving without every decision returning to you?</p>
            </div>
            <CtaButtons content={hero} />
          </div>
        </div>
      </section>
    </div>
  );
}
